import { useEffect, useState } from 'react';

import {
  AdvanceAmountCollectionResponse,
  GlobalAmountSimulationRecord,
  postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmountsSimulateGlobal,
} from '@eneco/dc-be/client';
import { useSWRConfig } from 'swr';

export const calculateReconcileIndividualAdvanceAmount = ({
  currentAmount,
  monthsUntilMeterReadingMonth,
  amountDueForMeterReadingMonth,
}: {
  currentAmount: number | null | undefined;
  monthsUntilMeterReadingMonth: number | null | undefined;
  amountDueForMeterReadingMonth: number | null | undefined;
}): number | null => {
  if (currentAmount == null || monthsUntilMeterReadingMonth == null || amountDueForMeterReadingMonth == null)
    return null;
  const userProjectedAmount = currentAmount * (monthsUntilMeterReadingMonth === 0 ? 1 : monthsUntilMeterReadingMonth);
  return userProjectedAmount - amountDueForMeterReadingMonth;
};

const sumCalculatedAmounts = (amounts: (number | null)[]): number | null => {
  const calculableAmounts = amounts.filter((amount): amount is number => amount != null);

  if (calculableAmounts.length === 0) return null;

  return calculableAmounts.reduce((total, amount) => total + amount, 0);
};

export const useCalculateReconcileGlobalAdvanceAmountWithCustomTotalAmount = ({
  customTotalAmount,
  advances,
  accountNumber,
  addressIdentifier,
}: {
  advances: AdvanceAmountCollectionResponse | null;
  customTotalAmount: number;
  accountNumber: string;
  addressIdentifier: string;
}) => {
  const { mutate } = useSWRConfig();
  const [amount, setAmount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
    const calcTotalAmountDue = (): number | null => {
      const totalAmountDue = sumCalculatedAmounts(
        (advances?.advancePayments ?? []).map(advance =>
          calculateReconcileIndividualAdvanceAmount({
            currentAmount: 0,
            amountDueForMeterReadingMonth: advance.details?.amountDueForMeterReadingMonth,
            monthsUntilMeterReadingMonth: advance.details?.monthsUntilMeterReadingMonth,
          }),
        ),
      );

      return totalAmountDue == null ? null : totalAmountDue * -1;
    };
    const calcCustomTotalAmountDue = (
      distributedAmounts: GlobalAmountSimulationRecord[] | null | undefined,
    ): number | null => {
      return sumCalculatedAmounts(
        (distributedAmounts ?? []).map(distributedAmount => {
          const monthsUntilMeterReadingMonth = advances?.advancePayments?.find(
            ap => ap.contractDetails?.contractNumber === distributedAmount.contractNumber,
          )?.details?.monthsUntilMeterReadingMonth;
          // To get the amount due, multiply each new distributed amount by its remaining months.
          if (distributedAmount.amount == null || monthsUntilMeterReadingMonth == null) return null;

          return distributedAmount.amount * monthsUntilMeterReadingMonth;
        }),
      );
    };

    const fetchAmount = async () => {
      try {
        setIsLoading(true);
        const { response, data } =
          await postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmountsSimulateGlobal(
            {
              path: { accountNumber, addressIdentifier },
              body: { amount: customTotalAmount },
            },
          );

        mutate('/accounts/{accountNumber}/delivery-addresses/{addressIdentifier}/advance-amounts/simulate/global');
        if (response.ok) {
          setIsLoading(false);
          const distributedAmounts = data?.data?.records;
          if (distributedAmounts?.every(da => da.amount != null)) {
            const customTotalAmountDue = calcCustomTotalAmountDue(distributedAmounts);
            const totalAmountDue = calcTotalAmountDue();

            if (customTotalAmountDue == null || totalAmountDue == null) setAmount(null);
            else setAmount(customTotalAmountDue - totalAmountDue);
          }
        } else throw new Error();
      } catch (e) {
        void e;
        setIsLoading(false);
        setAmount(null);
      }
    };

    const minAmount = advances?.totals?.minAmount ?? -Infinity;
    const maxAmount = advances?.totals?.maxAmount ?? Infinity;

    if (
      advances &&
      accountNumber &&
      addressIdentifier &&
      customTotalAmount &&
      customTotalAmount >= minAmount &&
      customTotalAmount <= maxAmount
    )
      fetchAmount();
    else setAmount(null);
  }, [advances, customTotalAmount, accountNumber, addressIdentifier, mutate]);

  return { amount, isLoading };
};

export const calculateReconcileGlobalWithExistingCurrentAmounts = ({
  advances,
}: {
  advances: AdvanceAmountCollectionResponse | null;
}): number | null => {
  if (!advances) return null;

  return sumCalculatedAmounts(
    (advances.advancePayments ?? []).map(advance =>
      calculateReconcileIndividualAdvanceAmount({
        currentAmount: advance.details?.currentAmount,
        amountDueForMeterReadingMonth: advance.details?.amountDueForMeterReadingMonth,
        monthsUntilMeterReadingMonth: advance.details?.monthsUntilMeterReadingMonth,
      }),
    ),
  );
};
